// CreatorRegiePanel.tsx — configurations de régie d'un créateur, dans l'onglet Ressources
//
// Même présentation que les autres panneaux (barre de filtres, bascule cartes / liste, mêmes
// cartes, même liste). La vignette est la mire d'attente de la chaîne de la configuration.
// « Télécharger » donne le dernier enregistrement de cette chaîne, quand il est sur le serveur.
// Données : admin-api AdminResourceItems (kind = regie).

import React, { useEffect, useMemo, useState } from 'react';
import { Download } from 'lucide-react';

import styles from './ChannelsSection.module.scss';
import extra  from './VideosSection.module.scss';
import { ListTable, TitleCell, WhenCell, RowActions, Dash } from './DashboardList';
import { Segment, ViewToggle, useView, plural, tintOf, dayLabel, hour } from './dashboardShared';
import { WWW_URLS } from '../../../backendServices/urls';
import { getAdminResourceItems, AdminResourceItem } from '../../../backendServices/adminManagment/api';

const VIEW_KEY = 'adminResources.regieView';

type Sort = 'recent' | 'name';
const SORTS: readonly (readonly [Sort, string])[] = [ [ 'recent', 'Plus récentes' ], [ 'name', 'Nom (A → Z)' ] ];
const byDateDesc = (a: AdminResourceItem, b: AdminResourceItem) => (b.date ?? '').localeCompare(a.date ?? '');
const COMPARE: Record<Sort, (a: AdminResourceItem, b: AdminResourceItem) => number> = {
  recent: byDateDesc,
  name  : (a, b) => a.title.localeCompare(b.title, 'fr', { sensitivity: 'base', numeric: true }),
};

const pendingOf = (r: AdminResourceItem) => r.channelPublicRef ? `${WWW_URLS.MediaServer}${r.channelPublicRef}/images/pending.webp` : '';

/** La mire d'attente de la chaîne ; à défaut, le nom de la configuration sur une couleur. */
const Pic: React.FC<{ r: AdminResourceItem; mini?: boolean }> = ({ r, mini }) => {
  const [ failed, setFailed ] = useState(false);
  const src = pendingOf(r);
  const img = src && !failed ? <img src={src} alt="" loading="lazy" onError={() => setFailed(true)} /> : null;
  if (mini) return <div style={{ width: '100%', height: '100%', background: tintOf(r.id) }}>{img}</div>;
  return (
    <div className={styles.thumb} style={{ background: tintOf(r.id) }}>
      {img ?? <span className={styles.thumbName}>{r.title}</span>}
    </div>
  );
};

const Dl: React.FC<{ r: AdminResourceItem }> = ({ r }) => (
  <div className={styles.actions}>
    {r.downloadUrl
      ? <a className={styles.secondary} href={r.downloadUrl} download target="_blank" rel="noreferrer"
           title="Télécharger le dernier enregistrement de cette chaîne" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
          <Download size={14} aria-hidden="true" />Dernier enregistrement
        </a>
      : <button type="button" className={styles.secondary} disabled
                title="Aucun enregistrement téléchargeable pour cette chaîne">Aucun enregistrement</button>}
  </div>
);

const CreatorRegiePanel: React.FC<{ adminId: number }> = ({ adminId }) => {

  const [ configs , setConfigs  ] = useState<AdminResourceItem[]>([]);
  const [ loading , setLoading  ] = useState(true);
  const [ error   , setError    ] = useState(false);
  const [ channel , setChannel  ] = useState<string>('all');
  const [ query   , setQuery    ] = useState('');
  const [ sort    , setSort     ] = useState<Sort>('recent');
  const [ view    , setView     ] = useView(VIEW_KEY);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(false);
    getAdminResourceItems(adminId, 'regie').then(r => {
      if (cancelled) return;
      if (r.code === 1) setConfigs(r.payload.items); else setError(true);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [adminId]);

  const channels = useMemo(() => {
    const count = new Map<string, number>();
    configs.forEach(c => { if (c.subtitle) count.set(c.subtitle, (count.get(c.subtitle) ?? 0) + 1); });
    return Array.from(count.entries()).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([n]) => n);
  }, [configs]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return configs.filter(c => (channel === 'all' || c.subtitle === channel) && (!q || `${c.title} ${c.subtitle}`.toLowerCase().includes(q))).sort(COMPARE[sort]);
  }, [configs, channel, query, sort]);

  if (loading) return <span>Chargement des configurations…</span>;
  if (error)   return <div className={styles.empty}>Chargement impossible.</div>;
  if (configs.length === 0) return <div className={styles.empty}>Aucune configuration de régie.</div>;

  const filtersOn = query.trim() !== '' || channel !== 'all';

  return (
    <div>
      <div className={styles.toolbar} style={{ marginBottom: 16 }}>
        <div className={extra.search}>
          <svg className={extra.searchIcon} width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="7" cy="7" r="5"/><path d="M11 11l4 4"/></svg>
          <input className={extra.searchInput} type="text" value={query} placeholder="Configuration, chaîne…" aria-label="Rechercher une configuration"
                 onChange={e => setQuery(e.target.value)} />
          {query && <button type="button" className={extra.searchClear} onClick={() => setQuery('')} aria-label="Effacer la recherche">✕</button>}
        </div>
        {channels.length > 1 &&
          <Segment label="Chaîne" value={channel} onChange={setChannel}
                   choices={[ ['all', 'Toutes les chaînes'] as const, ...channels.map(n => [n, n] as const) ]} />}
        {filtersOn && <>
          <span className={extra.count} aria-live="polite"><strong>{plural(shown.length, 'configuration')}</strong> sur {configs.length}</span>
          <button type="button" className={extra.reset} onClick={() => { setQuery(''); setChannel('all'); }}>Réinitialiser</button>
        </>}
        <span className={styles.spacer}/>
        <label className={extra.sort}>Trier par
          <select value={sort} onChange={e => setSort(e.target.value as Sort)}>
            {SORTS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </select>
        </label>
        <ViewToggle view={view} onChange={setView} />
      </div>

      {shown.length === 0 && <div className={styles.empty}>Aucune configuration ne correspond.</div>}

      {view === 'cards' && shown.length > 0 && (
        <div className={styles.cards}>
          {shown.map(r => (
            <article key={r.id} className={styles.card}>
              <Pic r={r} />
              <div className={styles.cardBody}>
                <span className={styles.cardName} title={r.title}>{r.title || '—'}</span>
                <div className={styles.chips}>
                  {r.subtitle && <span>{r.subtitle}</span>}
                  {r.date && <span>{dayLabel(r.date)} {hour(r.date)}</span>}
                </div>
                <Dl r={r} />
              </div>
            </article>
          ))}
        </div>
      )}

      {view === 'list' && shown.length > 0 && (
        <ListTable items={shown} rowKey={r => r.id} dateOf={r => r.date}
                   noun={{ one: 'configuration', many: 'configurations' }}
                   sort={sort === 'recent' ? null : sort} onSort={id => setSort((id ?? 'recent') as Sort)}
                   columns={[
          { id: 'name', label: 'Configuration', sort: COMPARE.name, cell: r =>
              <TitleCell thumb={<Pic r={r} mini />} title={r.title || '—'} titleHint={r.title} /> },
          { id: 'channel', label: 'Chaîne', cell: r => r.subtitle || <Dash/> },
          { id: 'date', label: 'Créée', cell: r => <WhenCell iso={r.date} /> },
          { id: 'actions', label: '', right: true, cell: r => <RowActions><Dl r={r} /></RowActions> },
        ]} />
      )}
    </div>
  );
};

export default CreatorRegiePanel;
